import { Box, Container, Paper, Grid, Card, CardContent, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Button, Typography } from '@sm-dashboard/ui-components';
import chabadLogo from '@/assets/chabad-logo1.png';

const AppHomepage = () => {
  const navigate = useNavigate();
  const features = [
    {
      title: 'Chabad Center Management',
      description: 'Comprehensive platform for managing Chabad centers, events, and community services across Israel.'
    },
    {
      title: 'Community Services',
      description: 'Connect community members with local Chabad centers for religious services, education, and support.'
    },
    {
      title: 'Event Coordination',
      description: 'Organize and manage community events, religious celebrations, and educational programs.'
    },
    {
      title: 'Contact Management',
      description: 'Maintain and organize contact information for community members and service providers.'
    }
  ];

  return (
    <Container
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: theme =>
          `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey.A200} 100%)`,
        padding: 4,
        textAlign: 'right !important',
        direction: 'rtl !important'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 4
        }}
      >
        <Box
          component="img"
          src={chabadLogo}
          alt="Chabad Logo"
          sx={{
            maxWidth: '300px',
            height: 'auto',
            '@media (max-width: 600px)': {
              maxWidth: '250px'
            }
          }}
        />
      </Box>

      <Paper
        elevation={3}
        sx={{
          padding: 6,
          borderRadius: '20px',
          boxShadow: theme => `0 20px 40px ${theme.palette.grey.A100}`,
          backgroundColor: theme => `rgba(${theme.palette.common.white}, 0.95)`,
          backdropFilter: 'blur(10px)',
          maxWidth: '900px',
          width: '100%'
        }}
      >
        <Box textAlign="center" mb={4}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h1" color="primary.main">
              שלוחים אפ
            </Typography>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" color="text.secondary">
              שלוחים דשבורד - פלטפורמה לניהול מרכזי חב"ד בישראל
            </Typography>
          </Box>
          <Box sx={{ maxWidth: '700px', mx: 'auto', mb: 4 }}>
            <Typography variant="body">
              A comprehensive platform designed to connect and manage Chabad centers throughout Israel. Our application
              facilitates community services, event coordination, and religious education while maintaining the highest
              standards of data protection and user privacy.
            </Typography>
          </Box>
        </Box>

        <Box mb={6}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" color="secondary.main">
              Application Features
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: '15px',
                    boxShadow: theme => `0 8px 25px ${theme.palette.grey.A100}`,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: theme => `0 15px 35px ${theme.palette.grey.A100}`
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: '0.35em' }}>
                      <Typography variant="h6" color="secondary.main">
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mb={4}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h3" color="secondary.main">
              Data Usage & Privacy
            </Typography>
          </Box>
          <Paper
            elevation={1}
            sx={{
              p: 4,
              backgroundColor: theme => theme.palette.background.paper,
              borderRadius: '15px'
            }}
          >
            <Box sx={{ mb: '0.35em' }}>
              <Typography variant="body">
                <strong>Transparency in Data Collection:</strong> Our application requests access to your Google account
                information solely for authentication and service delivery purposes. We collect and use your data to:
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 3, mb: 3 }}>
              <Box component="li" sx={{ mb: '0.35em' }}>
                <Typography variant="body">Authenticate your identity securely using Google OAuth</Typography>
              </Box>
              <Box component="li" sx={{ mb: '0.35em' }}>
                <Typography variant="body">Provide personalized access to Chabad center services</Typography>
              </Box>
              <Box component="li" sx={{ mb: '0.35em' }}>
                <Typography variant="body">Coordinate community events and religious services</Typography>
              </Box>
              <Box component="li" sx={{ mb: '0.35em' }}>
                <Typography variant="body">Maintain contact information for service coordination</Typography>
              </Box>
              <Box component="li" sx={{ mb: '0.35em' }}>
                <Typography variant="body">Send important updates about community events and services</Typography>
              </Box>
            </Box>
            <Typography variant="body">
              <strong>Data Protection:</strong> We implement industry-standard security measures to protect your
              personal information and never share your data with third parties without your explicit consent.
            </Typography>
          </Paper>
        </Box>

        <Box textAlign="center" mb={4}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h3" color="secondary.main">
              Ready to Get Started?
            </Typography>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body">
              Access the Chabad Dashboard to connect with your local community and access religious services.
            </Typography>
          </Box>
          <Button size="large" color="secondary" onClick={() => navigate('/login')}>
            Access Dashboard
          </Button>
        </Box>

        <Box
          textAlign="center"
          sx={{
            pt: 4,
            borderTop: 1,
            borderColor: 'divider',
            mt: 4
          }}
        >
          <Box sx={{ mb: '0.35em' }}>
            <Typography variant="body2" color="text.secondary">
              For questions about our privacy practices or data usage, please review our comprehensive privacy policy.
            </Typography>
          </Box>
          <Link
            href="/privacy-policy"
            variant="body1"
            sx={{
              color: theme => theme.palette.secondary.main,
              textDecoration: 'underline',
              fontWeight: 'bold',
              '&:hover': {
                color: theme => theme.palette.primary.dark
              }
            }}
          >
            View Privacy Policy
          </Link>
        </Box>

        <Box textAlign="center" sx={{ mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            © 2024 Chabad Dashboard. All rights reserved.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Contact: chabad@chabadisrael.co.il | +972-51-541-5770
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default AppHomepage;
